import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CAlert,
  CTable,
  CFormInput,
  CInputGroup,
  CBadge,
  CPagination,
  CPaginationItem,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilPeople, 
  cilMagnifyingGlass, 
  cilFilter, 
  cilReload,
  cilArrowRight,
  cilArrowLeft,
  cilPlus,
  cilPencil,
  cilTrash,
  cilOptions
} from '@coreui/icons';
import { superAdminAPI } from '../services/api';
import ResellerModal from '../components/ResellerModal'; // Import the modal

const SuperAdminResellers = () => {
  const navigate = useNavigate();
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'company_name', direction: 'asc' });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch all resellers
  const fetchResellers = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await superAdminAPI.getResellers();
      if (response.data.success) {
        setResellers(response.data.resellers);
      } else {
        setError('Failed to fetch resellers');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch resellers');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchResellers();
  }, []);

  // Handle create reseller
  const handleCreateReseller = async (resellerData) => {
    setActionLoading(true);
    try {
      const response = await superAdminAPI.createReseller(resellerData);
      if (response.data.success) {
        setSuccessMessage('Reseller created successfully!');
        fetchResellers(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to create reseller');
      }
    } catch (error) {
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update reseller
  const handleUpdateReseller = async (resellerData) => {
    setActionLoading(true);
    try {
      const response = await superAdminAPI.updateReseller(selectedReseller._id, resellerData);
      if (response.data.success) {
        setSuccessMessage('Reseller updated successfully!');
        fetchResellers(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to update reseller');
      }
    } catch (error) {
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete reseller
  const handleDeleteReseller = async () => {
    setActionLoading(true);
    try {
      const response = await superAdminAPI.deleteReseller(selectedReseller._id);
      if (response.data.success) {
        setSuccessMessage('Reseller deleted successfully!');
        fetchResellers(); // Refresh the list
        setShowDeleteModal(false);
      } else {
        setError(response.data.error || 'Failed to delete reseller');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete reseller');
    } finally {
      setActionLoading(false);
      setSelectedReseller(null);
    }
  };

  // Open edit modal
  const openEditModal = async (reseller) => {
    setSelectedReseller(reseller);
    try {
      // Fetch fresh data for editing
      const response = await superAdminAPI.getSingleResellerid(reseller._id);
      if (response.data.success) {
        setSelectedReseller(response.data.reseller);
        setShowEditModal(true);
      }
    } catch (err) {
      setError('Failed to fetch reseller details');
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (reseller) => {
    setSelectedReseller(reseller);
    setShowDeleteModal(true);
  };

  // Filter resellers based on search term
  const filteredResellers = resellers.filter(reseller => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (reseller.company_name && reseller.company_name.toLowerCase().includes(searchLower)) ||
      (reseller.admin_login_email && reseller.admin_login_email.toLowerCase().includes(searchLower)) ||
      (reseller.resellerid && reseller.resellerid.toLowerCase().includes(searchLower)) ||
      (reseller.location && reseller.location.toLowerCase().includes(searchLower)) ||
      (reseller.admin_contact && reseller.admin_contact.toLowerCase().includes(searchLower))
    );
  });

  // Sort resellers
  const sortedResellers = [...filteredResellers].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentResellers = sortedResellers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedResellers.length / itemsPerPage);

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle page change
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go back to dashboard
  const goBack = () => {
    navigate('/dashboard');
  };

  return (
    <div>
      {/* Header with back button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <CButton 
            color="secondary" 
            variant="outline" 
            className="me-3"
            onClick={goBack}
          >
            ← Back to Dashboard
          </CButton>
          <div>
            <h2>
              <CIcon icon={cilPeople} className="me-2 text-primary" />
              All Resellers
            </h2>
            <p className="text-medium-emphasis">
              Manage and view all reseller accounts ({resellers.length} total)
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <CButton color="primary" onClick={() => setShowAddModal(true)}>
            <CIcon icon={cilPlus} className="me-2" />
            Add New Reseller
          </CButton>
          <CButton color="secondary" onClick={fetchResellers}>
            <CIcon icon={cilReload} className="me-2" />
            Refresh
          </CButton>
        </div>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <CAlert color="success" className="mb-3" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </CAlert>
      )}

      {/* Error Alert */}
      {error && (
        <CAlert color="danger" className="mb-3" onClose={() => setError('')} dismissible>
          {error}
        </CAlert>
      )}

      {/* Search and Filters */}
      <CCard className="mb-4">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol md={6}>
              <CInputGroup>
                <CFormInput
                  placeholder="Search resellers by name, email, ID, or location..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                />
                <CButton color="primary" type="button">
                  <CIcon icon={cilMagnifyingGlass} />
                </CButton>
              </CInputGroup>
            </CCol>
            <CCol md={6} className="text-end">
              <span className="text-medium-emphasis me-3">
                Showing {currentResellers.length} of {filteredResellers.length} resellers
              </span>
              <CButton color="secondary" variant="outline">
                <CIcon icon={cilFilter} className="me-2" />
                Filters
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <CSpinner color="primary" />
          <p className="mt-2">Loading resellers...</p>
        </div>
      ) : (
        <>
          {/* Resellers Table */}
          <CCard>
            <CCardHeader>
              <h5>Reseller Directory</h5>
            </CCardHeader>
            <CCardBody>
              {currentResellers.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-medium-emphasis">No resellers found</p>
                  {searchTerm && (
                    <CButton 
                      color="link" 
                      onClick={() => setSearchTerm('')}
                    >
                      Clear search
                    </CButton>
                  )}
                </div>
              ) : (
                <>
                  <CTable striped hover responsive>
                    <thead>
                      <tr>
                        <th>Reseller ID</th>
                        <th>Company Name</th>
                        <th>Email</th>
                        <th>Margin (%)</th>
                        <th>Location</th>
                        <th>Contact Person</th>
                        <th>Payment Mode</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentResellers.map(reseller => (
                        <tr key={reseller._id}>
                          <td>
                            <CBadge color="primary">{reseller.resellerid || 'N/A'}</CBadge>
                          </td>
                          <td>
                            <strong>{reseller.company_name || 'N/A'}</strong>
                          </td>
                          <td>
                            <a href={`mailto:${reseller.admin_login_email}`} className="text-decoration-none">
                              {reseller.admin_login_email || 'N/A'}
                            </a>
                          </td>
                          <td>
                            <CBadge color={reseller.margin >= 25 ? 'success' : 'info'}>
                              {reseller.margin || 0}%
                            </CBadge>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }} title={reseller.location}>
                              {reseller.location || 'N/A'}
                            </div>
                          </td>
                          <td>
                            {reseller.admin_contact || 'N/A'}
                          </td>
                          <td>
                            <CBadge color={reseller.payment_mode === 'Net 30' ? 'success' : 
                                      reseller.payment_mode === 'Net 60' ? 'warning' : 'secondary'}>
                              {reseller.payment_mode || 'N/A'}
                            </CBadge>
                          </td>                    
                          <td>
                            <CDropdown>
                              <CDropdownToggle color="secondary" size="sm" variant="ghost">
                                <CIcon icon={cilOptions} />
                              </CDropdownToggle>
                              <CDropdownMenu>
                                <CDropdownItem onClick={() => openEditModal(reseller)}>
                                  <CIcon icon={cilPencil} className="me-2" />
                                  Edit
                                </CDropdownItem>
                                <CDropdownItem 
                                  onClick={() => openDeleteModal(reseller)}
                                  className="text-danger"
                                >
                                  <CIcon icon={cilTrash} className="me-2" />
                                  Delete
                                </CDropdownItem>
                              </CDropdownMenu>
                            </CDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </CTable>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <CPagination>
                        <CPaginationItem 
                          aria-label="Previous" 
                          disabled={currentPage === 1}
                          onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        >
                          <CIcon icon={cilArrowLeft} />
                        </CPaginationItem>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNumber;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }
                          
                          return (
                            <CPaginationItem
                              key={pageNumber}
                              active={currentPage === pageNumber}
                              onClick={() => paginate(pageNumber)}
                              style={{ cursor: 'pointer' }}
                            >
                              {pageNumber}
                            </CPaginationItem>
                          );
                        })}
                        
                        <CPaginationItem 
                          aria-label="Next" 
                          disabled={currentPage === totalPages}
                          onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                          style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        >
                          <CIcon icon={cilArrowRight} />
                        </CPaginationItem>
                      </CPagination>
                    </div>
                  )}

                  {/* Page Info */}
                  <div className="text-center mt-3">
                    <p className="text-medium-emphasis">
                      Page {currentPage} of {totalPages} • Showing {currentResellers.length} resellers per page
                    </p>
                  </div>
                </>
              )}
            </CCardBody>
          </CCard>
        </>
      )}

      {/* Add Reseller Modal */}
      <ResellerModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleCreateReseller}
        mode="create"
      />

      {/* Edit Reseller Modal */}
      <ResellerModal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedReseller(null);
        }}
        onSave={handleUpdateReseller}
        reseller={selectedReseller}
        mode="edit"
      />

      {/* Delete Confirmation Modal */}
      <CModal 
        visible={showDeleteModal} 
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedReseller(null);
        }}
      >
        <CModalHeader>
          <CModalTitle>Confirm Delete</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedReseller && (
            <>
              <p>Are you sure you want to delete the following reseller?</p>
              <div className="alert alert-warning">
                <strong>{selectedReseller.company_name}</strong><br />
                ID: {selectedReseller.resellerid}<br />
                Email: {selectedReseller.admin_login_email}
              </div>
              <p className="text-danger">
                <strong>Warning:</strong> This action cannot be undone. 
                Make sure the reseller has no clients or active subscriptions before deleting.
              </p>
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton 
            color="secondary" 
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedReseller(null);
            }}
            disabled={actionLoading}
          >
            Cancel
          </CButton>
          <CButton 
            color="danger" 
            onClick={handleDeleteReseller}
            disabled={actionLoading}
          >
            {actionLoading ? 'Deleting...' : 'Delete Reseller'}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default SuperAdminResellers;
